import React from 'react';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';

import {
  DEPLOYMENT_ACTIVE_STATES,
  DEPLOYMENT_ERROR_STATES,
  DEPLOYMENT_INACTIVE_STATES,
} from 'types/deployment';
import { SystemStateWidget as SystemStateWidgetType } from 'types/dashboard_widget';

import { useAPI } from 'hooks/api/useAPI';
import { useCameras } from 'cameras/hooks/useCameras';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useGateways } from 'gateways/hooks/useGateways';
import { useStreams } from 'streams/hooks/useStreams';

import { Card } from 'components/Card/Card';
import { Heading } from 'components/Heading/Heading';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import { DashboardNumber } from 'application/views/Overview/Number/Number';

import styles from './SystemStateWidget.module.scss';

const c = classNames.bind(styles);

export function SystemStateWidget(widget: SystemStateWidgetType) {
  const { applicationID } = useAPI();

  // Fetch cameras by status
  const { data: onlineCameras } = useCameras({
    params: { limit: 1, statuses: ['online'] },
    keepPreviousData: true,
  });

  const { data: offlineCameras } = useCameras({
    params: { limit: 1, statuses: ['offline', 'unauthorized'] },
    keepPreviousData: true,
  });

  const { data: unknownCameras } = useCameras({
    params: { limit: 1, statuses: ['unknown'] },
    keepPreviousData: true,
  });

  // Fetch input streams by status (uri_stream but not stream_type 'file')
  const { data: onlineStreams } = useStreams({
    params: {
      sources: ['uri_stream'],
      stream_types: ['rtsp', 'webrtc'],
      limit: 1,
      status: ['online'],
    },
    keepPreviousData: true,
  });

  const { data: offlineStreams } = useStreams({
    params: {
      sources: ['uri_stream'],
      stream_types: ['rtsp', 'webrtc'],
      limit: 1,
      status: ['offline'],
    },
    keepPreviousData: true,
  });

  const { data: unknownStreams } = useStreams({
    params: {
      sources: ['uri_stream'],
      stream_types: ['rtsp', 'webrtc'],
      limit: 1,
      status: ['unknown'],
    },
    keepPreviousData: true,
  });

  // Fetch all deployments
  const { data: activeDeployments } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_ACTIVE_STATES],
    },
    keepPreviousData: true,
  });

  const { data: stoppedDeployments } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_INACTIVE_STATES],
    },
    keepPreviousData: true,
  });

  const { data: errorDeployments } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_ERROR_STATES],
    },
    keepPreviousData: true,
  });

  // Fetch gateways by status
  const { data: onlineGateways } = useGateways({
    params: { limit: 1, statuses: ['online'] },
    keepPreviousData: true,
  });

  const { data: offlineGateways } = useGateways({
    params: { limit: 1, statuses: ['offline'] },
    keepPreviousData: true,
  });

  const { data: inProgressGateways } = useGateways({
    params: { limit: 1, statuses: ['starting', 'stopping', 'pending'] },
    keepPreviousData: true,
  });

  // Camera counts
  const cameraCounts = {
    online: onlineCameras?.total_elements ?? 0,
    offline: offlineCameras?.total_elements ?? 0,
    unknown: unknownCameras?.total_elements ?? 0,
  };

  // Stream counts
  const streamCounts = {
    online: onlineStreams?.total_elements ?? 0,
    offline: offlineStreams?.total_elements ?? 0,
    unknown: unknownStreams?.total_elements ?? 0,
  };

  // Deployment counts
  const deploymentCounts = {
    online: activeDeployments?.total_elements ?? 0,
    offline: errorDeployments?.total_elements ?? 0,
    stopped: stoppedDeployments?.total_elements ?? 0,
  };

  // Gateway counts
  const gatewayCounts = {
    online: onlineGateways?.total_elements ?? 0,
    offline: offlineGateways?.total_elements ?? 0,
    inProgress: inProgressGateways?.total_elements ?? 0,
  };

  return (
    <Widget widget={widget}>
      <div className={c('wrap')}>
        <Card className={c('card')}>
          <Heading level="4">Cameras</Heading>
          <div className={c('numbers')}>
            <DashboardNumber
              number={cameraCounts.online}
              status="success"
              to={`/applications/${applicationID}/deploy/cameras`}
              state={{ filters: { status: 'online' } }}
            />
            <DashboardNumber
              number={cameraCounts.offline}
              status="offline"
              to={`/applications/${applicationID}/deploy/cameras`}
              state={{ filters: { status: 'offline' } }}
            />
            <DashboardNumber
              number={cameraCounts.unknown}
              status="warning"
              to={`/applications/${applicationID}/deploy/cameras`}
              state={{ filters: { status: 'unknown' } }}
            />
          </div>
          <div className={c('graph')} aria-hidden="true">
            {cameraCounts.online > 0 && (
              <Tooltip
                side="top"
                content={`${cameraCounts.online} cameras online`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': cameraCounts.online } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme success')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/cameras`}
                      state={{ filters: { status: 'online' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {cameraCounts.offline > 0 && (
              <Tooltip
                side="top"
                content={`${cameraCounts.offline} cameras offline`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': cameraCounts.offline } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme danger')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/cameras`}
                      state={{
                        filters: { status: 'offline' },
                      }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {cameraCounts.unknown > 0 && (
              <Tooltip
                side="top"
                content={`${cameraCounts.unknown} cameras unknown`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': cameraCounts.unknown } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme warning')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/cameras`}
                      state={{ filters: { status: 'unknown' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </Card>

        <Card className={c('card')}>
          <Heading level="4">Streams</Heading>
          <div className={c('numbers')}>
            <DashboardNumber
              number={streamCounts.online}
              status="success"
              to={`/applications/${applicationID}/deploy/streams`}
              state={{ filters: { status: 'online' } }}
            />
            <DashboardNumber
              number={streamCounts.offline}
              status="offline"
              to={`/applications/${applicationID}/deploy/streams`}
              state={{ filters: { status: 'offline' } }}
            />
            <DashboardNumber
              number={streamCounts.unknown}
              status="warning"
              to={`/applications/${applicationID}/deploy/streams`}
              state={{ filters: { status: 'unknown' } }}
            />
          </div>
          <div className={c('graph')} aria-hidden="true">
            {streamCounts.online > 0 && (
              <Tooltip
                side="top"
                content={`${streamCounts.online} streams online`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': streamCounts.online } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme success')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/streams`}
                      state={{ filters: { status: 'online' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {streamCounts.offline > 0 && (
              <Tooltip
                side="top"
                content={`${streamCounts.offline} streams offline`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': streamCounts.offline } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme danger')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/streams`}
                      state={{ filters: { status: 'offline' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {streamCounts.unknown > 0 && (
              <Tooltip
                side="top"
                content={`${streamCounts.unknown} streams unknown`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': streamCounts.unknown } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme warning')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/streams`}
                      state={{ filters: { status: 'unknown' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </Card>

        <Card className={c('card')}>
          <Heading level="4">Workflows</Heading>
          <div className={c('numbers')}>
            <DashboardNumber
              number={deploymentCounts.online}
              status="running"
              to={`/applications/${applicationID}/deploy/deployments`}
              state={{ filters: { state: 'running' } }}
            />
            <DashboardNumber
              number={deploymentCounts.offline}
              status="offline"
              to={`/applications/${applicationID}/deploy/deployments`}
              state={{ filters: { state: 'error' } }}
            />
            <DashboardNumber
              number={deploymentCounts.stopped}
              status="stopped"
              to={`/applications/${applicationID}/deploy/deployments`}
              state={{ filters: { state: 'stopped' } }}
            />
          </div>
          <div className={c('graph')} aria-hidden="true">
            {deploymentCounts.online > 0 && (
              <Tooltip
                side="top"
                content={`${deploymentCounts.online} deployments running`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    {
                      '--count': deploymentCounts.online,
                    } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme success')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/deployments`}
                      state={{ filters: { state: 'running' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {deploymentCounts.offline > 0 && (
              <Tooltip
                side="top"
                content={`${deploymentCounts.offline} deployments in error state`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    {
                      '--count': deploymentCounts.offline,
                    } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme danger')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/deployments`}
                      state={{ filters: { state: 'error' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {deploymentCounts.stopped > 0 && (
              <Tooltip
                side="top"
                content={`${deploymentCounts.stopped} deployments stopped`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    {
                      '--count': deploymentCounts.stopped,
                    } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme warning')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/deployments`}
                      state={{ filters: { state: 'stopped' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </Card>

        <Card className={c('card')}>
          <Heading level="4">Gateways</Heading>
          <div className={c('numbers')}>
            <DashboardNumber
              number={gatewayCounts.online}
              status="success"
              to={`/applications/${applicationID}/deploy/gateways`}
              state={{ filters: { status: 'online' } }}
            />
            <DashboardNumber
              number={gatewayCounts.offline}
              status="offline"
              to={`/applications/${applicationID}/deploy/gateways`}
              state={{ filters: { status: 'offline' } }}
            />
            <DashboardNumber
              number={gatewayCounts.inProgress}
              status="warning"
              to={`/applications/${applicationID}/deploy/gateways`}
              state={{
                filters: { status: 'starting' },
              }}
            />
          </div>
          <div className={c('graph')} aria-hidden="true">
            {gatewayCounts.online > 0 && (
              <Tooltip
                side="top"
                content={`${gatewayCounts.online} gateways online`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': gatewayCounts.online } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme success')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/gateways`}
                      state={{ filters: { status: 'online' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {gatewayCounts.offline > 0 && (
              <Tooltip
                side="top"
                content={`${gatewayCounts.offline} gateways offline`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    { '--count': gatewayCounts.offline } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme danger')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/gateways`}
                      state={{ filters: { status: 'offline' } }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
            {gatewayCounts.inProgress > 0 && (
              <Tooltip
                side="top"
                content={`${gatewayCounts.inProgress} gateways in progress`}
              >
                <div
                  className={c('graph-line')}
                  style={
                    {
                      '--count': gatewayCounts.inProgress,
                    } as React.CSSProperties
                  }
                >
                  <div className={c('graph-line-fill', 'theme warning')}>
                    <Link
                      className={c('graph-link')}
                      to={`/applications/${applicationID}/deploy/gateways`}
                      state={{
                        filters: {
                          status: 'starting',
                        },
                      }}
                    />
                  </div>
                </div>
              </Tooltip>
            )}
          </div>
        </Card>
      </div>
    </Widget>
  );
}
